import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faArrowLeft,
  faSearch,
  faCheckCircle,
  faCircleXmark,
  faClock,
  faEye,
  faShoppingBag,
  faFilter,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

const HerKartMyOrders = () => {
  const { fetchMyOrders } = useHerKart();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");

  const load = async () => {
    setLoading(true);
    try {
      const response = await fetchMyOrders();
      if (response?.success) {
        setOrders(response.orders || []);
      }
    } catch (e) {
      console.error("My orders fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredOrders = useMemo(() => {
    let list = [...orders];

    if (status !== "All") {
      list = list.filter((order) => (order.status || "Pending") === status);
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((order) =>
        [order.orderNo, ...(order.items || []).map((item) => item.name)]
          .join(" ")
          .toLowerCase()
          .includes(keyword)
      );
    }

    return list.sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt));
  }, [orders, search, status]);

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <Link
              to="/foundher"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </Link>

            <div className="flex flex-col justify-start">
              <h1 className="text-xl font-bold text-left">My Orders</h1>
              <p className="text-xs text-slate-200">Your order history on FoundHer</p>
            </div>
          </div>

          <div className="rounded-xl bg-pink-600/20 px-4 py-2 text-pink-300">
            <span className="font-semibold">{filteredOrders.length} Orders</span>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-6">
        <div className="rounded-3xl border border-white/10 bg-white/5 p-5">
          <div className="grid gap-4 lg:grid-cols-2">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
              />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search order no. or product..."
                className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 outline-none focus:border-pink-500"
              />
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 text-sm text-slate-400">
                <FontAwesomeIcon icon={faFilter} />
                Status
              </div>

              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="flex-1 rounded-xl border border-white/10 bg-[#0b1120] px-2 py-1 outline-none focus:border-pink-500"
              >
                <option>All</option>
                <option>Pending</option>
                <option>Partially Delivered</option>
                <option>Delivered</option>
                <option>Cancelled</option>
              </select>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-4">
          {loading ? (
            <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-16 text-center text-slate-400">
              Loading your orders...
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="rounded-3xl border border-white/10 bg-white/5 px-6 py-16 text-center">
              <FontAwesomeIcon icon={faShoppingBag} className="mx-auto mb-5 text-6xl text-slate-500" />
              <h2 className="text-2xl text-cyan-200 font-bold">No Orders Yet</h2>
              <p className="mt-3 text-slate-400">
                {orders.length === 0
                  ? "You haven't placed any orders yet."
                  : "No orders match the current search or filter."}
              </p>
              <Link
                to="/herkart/products"
                className="mt-6 inline-flex items-center rounded-xl bg-pink-600 px-6 py-3 font-semibold text-white transition hover:bg-pink-500"
              >
                <FontAwesomeIcon icon={faShoppingBag} className="mr-2" />
                Start Shopping
              </Link>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div
                key={order._id || order.orderNo}
                className="rounded-3xl border border-white/10 bg-white/5 p-5 transition hover:border-pink-500/40 hover:bg-white/[0.07]"
              >
                <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-3">
                      <h2 className="text-lg text-cyan-200 font-bold">{order.orderNo}</h2>

                      <span
                        className={`rounded-full px-3 py-1 text-xs font-semibold ${
                          (order.status || "Pending") === "Delivered"
                            ? "bg-emerald-500/20 text-emerald-300"
                            : (order.status || "Pending") === "Cancelled"
                            ? "bg-red-500/20 text-red-300"
                            : (order.status || "Pending") === "Partially Delivered"
                            ? "bg-orange-500/20 text-orange-300"
                            : "bg-yellow-500/20 text-yellow-300"
                        }`}
                      >
                        {(order.status || "Pending") === "Delivered" && (
                          <FontAwesomeIcon icon={faCheckCircle} className="mr-1 inline" />
                        )}
                        {(order.status || "Pending") === "Cancelled" && (
                          <FontAwesomeIcon icon={faCircleXmark} className="mr-1 inline" />
                        )}
                        {(order.status || "Pending") === "Pending" && (
                          <FontAwesomeIcon icon={faClock} className="mr-1 inline" />
                        )}
                        {order.status || "Pending"}
                      </span>
                    </div>

                    <p className="mt-2 text-xs text-slate-400">
                      Ordered on {new Date(order.orderedAt).toLocaleDateString()}
                    </p>

                    <div className="mt-5 rounded-2xl border border-white/10 bg-[#111827]/70 p-4">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-pink-400">
                        Items
                      </p>

                      <div className="space-y-2">
                        {(order.items || []).slice(0, 3).map((item, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <span className="text-slate-200">
                              {item.name}
                              {item.store ? ` — ${item.store}` : ""}
                            </span>
                            <span className="text-slate-400">
                              {item.quantity} × ₹{item.price}
                            </span>
                          </div>
                        ))}

                        {(order.items || []).length > 3 && (
                          <p className="text-xs text-pink-400">
                            + {order.items.length - 3} more item(s)
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col items-start gap-4 lg:items-end">
                    <div className="text-right">
                      <p className="text-xs uppercase tracking-wide text-slate-500">Order Value</p>
                      <p className="mt-1 text-2xl font-bold text-pink-400">₹{order.grandTotal}</p>
                      <p className="mt-1 text-sm text-slate-400">
                        {order.totalItems} item{order.totalItems > 1 ? "s" : ""}
                      </p>
                    </div>

                    <Link
                      to={`/herkart/orders/${order.orderNo}`}
                      className="flex items-center gap-2 rounded-xl bg-pink-600 px-5 py-3 font-medium transition hover:bg-pink-500"
                    >
                      <FontAwesomeIcon icon={faEye} />
                      View Order
                    </Link>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HerKartMyOrders;
